import oktaAuth from '../config/okta';

const API_URL = process.env.REACT_APP_API_URL;
const USE_MOCK = process.env.REACT_APP_USE_MOCK === 'true';

// Mock data for local testing
const mockTerminations = [
  {
    ticket_key: 'ONBOFFBSD-1234',
    terminated_user: 'john.doe@instacart.com',
    processing_time: new Date(Date.now() + 3600000).toISOString(), // 1 hour from now
    support_user: 'manager@instacart.com',
    lit_hold: false,
    processed: 'False'
  },
  {
    ticket_key: 'ONBOFFBSD-1235',
    terminated_user: 'jane.smith@instacart.com',
    processing_time: new Date(Date.now() + 7200000).toISOString(), // 2 hours from now
    support_user: 'hr.admin@instacart.com',
    lit_hold: true,
    processed: 'False'
  },
  {
    ticket_key: 'ONBOFFBSD-1236',
    terminated_user: 'bob.wilson@instacart.com',
    processing_time: new Date(Date.now() + 10800000).toISOString(), // 3 hours from now
    support_user: 'manager@instacart.com',
    lit_hold: false,
    processed: 'False'
  }
];

// In-memory store for mock mode
let mockStore = [...mockTerminations];

// Mock API implementation
const mockApi = {
  async getTerminations() {
    await new Promise(resolve => setTimeout(resolve, 500)); // Simulate network delay
    return {
      terminations: mockStore.filter(t => t.processed === 'False'),
      count: mockStore.filter(t => t.processed === 'False').length
    };
  },

  async deleteTermination(ticketKey) {
    await new Promise(resolve => setTimeout(resolve, 300));
    const item = mockStore.find(t => t.ticket_key === ticketKey);
    if (!item) {
      throw new Error(`Termination ${ticketKey} not found`);
    }
    item.processed = 'True';
    item.removed_by = 'mock-user@instacart.com';
    item.removed_at = new Date().toISOString();
    return {
      message: `Termination ${ticketKey} removed from queue`,
      termination: item
    };
  },

  async updateTermination(ticketKey, updates) {
    await new Promise(resolve => setTimeout(resolve, 300));
    const item = mockStore.find(t => t.ticket_key === ticketKey);
    if (!item) {
      throw new Error(`Termination ${ticketKey} not found`);
    }
    if (updates.processing_time) {
      item.processing_time = updates.processing_time;
    }
    item.updated_by = 'mock-user@instacart.com';
    item.updated_at = new Date().toISOString();
    return {
      message: `Processing time updated for ${ticketKey}`,
      termination: item
    };
  }
};

// Real API implementation
async function getAuthHeaders() {
  const accessToken = await oktaAuth.tokenManager.get('accessToken');
  if (!accessToken || !accessToken.accessToken) {
    throw new Error('No access token available');
  }
  return {
    'Authorization': `Bearer ${accessToken.accessToken}`,
    'Content-Type': 'application/json'
  };
}

async function handleResponse(response) {
  const data = await response.json();
  if (!response.ok) {
    throw new Error(data.error || `HTTP error ${response.status}`);
  }
  return data;
}

const realApi = {
  async getTerminations() {
    const response = await fetch(`${API_URL}/terminations`, {
      headers: await getAuthHeaders()
    });
    return handleResponse(response);
  },

  async deleteTermination(ticketKey) {
    const response = await fetch(`${API_URL}/terminations/${encodeURIComponent(ticketKey)}`, {
      method: 'DELETE',
      headers: await getAuthHeaders()
    });
    return handleResponse(response);
  },

  async updateTermination(ticketKey, updates) {
    const response = await fetch(`${API_URL}/terminations/${encodeURIComponent(ticketKey)}`, {
      method: 'PATCH',
      headers: await getAuthHeaders(),
      body: JSON.stringify(updates)
    });
    return handleResponse(response);
  }
};

// Export the appropriate API based on environment
export const api = USE_MOCK ? mockApi : realApi;
